firebird.factory('videoMediaService', [
    'logService',
    'configService',
    'meetingService',
    'videoGlobalService',
    'sdpTransformService',
    'analyticsWrapperService',
    function (
        logService,
        configService,
        meetingService,
        videoGlobalService,
        sdpTransformService,
        analyticsWrapperService
    ) {
        var service = {
            initialize: initialize
        };

        var self;

        const logger = new LogServiceWrapper(logService, '[videoroomCtrl] videoMediaService');

        return service;

        function initialize(selfParam) {
            self = selfParam;

            // whether video is enabled (unmuted) in the UI or not. This will trigger the right media in offer.
            self.videoEnabled = false;
            // use this flag to determine if the video is turned on for the first time or not
            self.videoEnabledFirstTime = true;

            // video codec if not default, from URL param or user pair. Default is to use vp8.
            // possible values are auto, vp8, vp9, h264, null
            // if videocodec is set, it applies to both video and screen+file share,
            // unless overridden by videocodecFile and videocodecScreen.
            self.videocodec = null;
            self.videocodecScreen = null;
            self.videocodecFile = null;

            // similarly for audio codecs. possible values are opus, pcmu, pcma, g722, isac16, isac32.
            self.audiocodec = null;
            self.audiocodecFile = null;

            // framerate, default 0 indicates 30 for video publish; and 15 for screen share.
            // This can be altered using the frameRate URL parameter, which applies to both video and screen share.
            // Alternatively, if frameRateScreen URL parameter is used, then separate one applies to screen share.
            self.frameRate = 0;
            self.frameRateScreen = 0;
            self.frameRateFile = 0;

            // default max quantization, can be changed using maxQuant URL param.
            self.maxQuantVideo = 50;
            self.maxQuantScreen = 40;

            // addition constriants supplied to RTCPeerConnection. Can be changed using URL param.
            self.rtcConstraintsVideo = null;
            self.rtcConstraintsAudio = null;
            self.rtcConstraintsScreen = null;

            self.initMedia = initMedia;

            // can be true, false or "test".
            self.useSdpParser = true;
            self.customizeSdp = customizeSdp;
            self.customizeSdpSubscribe = customizeSdpSubscribe;
            self.enableAvailableIncomingBitrate = true;
            self.audioNacks = false;

            self.audioStereo = false; // whether to enable stereo audio.

            self.parseRtcConstraints = parseRtcConstraints;

            // profile-level-id config for h264.
            // usually 42e01f, but may be something else (42e028) on VDI OSX. For compatibility ignore level-id.
            self.h264ProfileLevel = { video: '42e0', screen: '42e0' };
            self.getH264ProfileLevelAttr = getH264ProfileLevelAttr;

            // ANYAM-109183 - workaround for version 115: use fps 15 for screen publish, and do not set playing attribute for screen view.
            self.fixScreenOn115 = true;
        }

        // update state from URL param
        function initMedia() {
            self.fixScreenOn115 = configService.getValue('fixScreenOn115') !== 'false';

            // update framerate from URL param
            var frameRate = configService.getValue('frameRate');
            if (frameRate) {
                frameRate = parseInt(frameRate);
                if (!isNaN(frameRate)) {
                    self.frameRate = frameRate;
                }
            }

            // update screen share framerate from URL param
            var frameRateScreen = configService.getValue('frameRateScreen');
            if (frameRateScreen) {
                frameRateScreen = parseInt(frameRateScreen);
                if (!isNaN(frameRateScreen)) {
                    self.frameRateScreen = frameRateScreen;
                }
            }

            // update max file play framerate from URL param
            var frameRateFile = configService.getValue('frameRateFile');
            if (frameRateFile) {
                frameRateFile = parseInt(frameRateFile);
                if (!isNaN(frameRateFile)) {
                    self.frameRateFile = frameRateFile;
                }
            }

            // use user pair first, but prefer url param if present.
            self.videocodec = configService.getServerValue('videocodec');
            if (getSupportedVideoCodec().indexOf(self.videocodec) < 0) {
                logger.error(
                    'applyQueryParameters',
                    'Unsupported video codec. Using browser default.',
                    self.videocodec
                );
                self.videocodec = null;
            }

            // read videocodec from URL param.
            ['videocodec', 'videocodecScreen', 'videocodecFile'].forEach(function (name) {
                var videocodec = configService.getClientValue(name);
                if (videocodec && getSupportedVideoCodec().indexOf(videocodec) >= 0) {
                    self[name] = videocodec;
                } else if (videocodec) {
                    logger.error('applyQueryParameters', `Unsupported ${name} parameter, ignored`, videocodec);
                }
            });

            // use user pair first, but prefer url param if present.
            self.audiocodec = configService.getServerValue('audiocodec') || null;

            // read audiocodec from URL param.
            ['audiocodec', 'audiocodecFile'].forEach(function (name) {
                var audiocodec = configService.getClientValue(name);
                if (audiocodec && ['opus', 'pcmu', 'pcma', 'g722', 'isac16', 'isac32'].indexOf(audiocodec) >= 0) {
                    logService.log('setting ' + name + ' to ' + audiocodec);
                    self[name] = audiocodec;
                } else if (audiocodec) {
                    logger.error('applyQueryParameters', `Unsupported ${name} parameter, ignored`, audiocodec);
                }
            });

            self.h264ProfileLevel.video = configService.getValue('h264Profile') || '42e0';
            self.h264ProfileLevel.screen = configService.getValue('h264ProfileScreen') || '42e0';

            var useSdpParser = configService.getValue('useSdpParser');
            if (useSdpParser) {
                self.useSdpParser = useSdpParser == 'true' ? true : useSdpParser == 'false' ? false : useSdpParser;
            }

            var enableAvailableIncomingBitrate = configService.getValue('enableAvailableIncomingBitrate');
            if (enableAvailableIncomingBitrate) {
                self.enableAvailableIncomingBitrate = enableAvailableIncomingBitrate == 'true'; // default is false.
            }

            // update max quantization from URL params.
            var maxQuant = configService.getClientValue('maxQuant');
            if (maxQuant) {
                var old1 = self.maxQuantVideo,
                    old2 = self.maxQuantScreen;
                self.maxQuantVideo = self.maxQuantScreen = parseInt(maxQuant);
                if (isNaN(self.maxQuantVideo)) {
                    self.maxQuantVideo = old1;
                    self.maxQuantScreen = old2;
                }
            }

            // update other vendor specific media constraints from URL params.
            self.rtcConstraintsVideo = self.parseRtcConstraints(configService.getClientValue('rtcConstraintsVideo'));
            self.rtcConstraintsAudio = self.parseRtcConstraints(configService.getClientValue('rtcConstraintsAudio'));
            self.rtcConstraintsScreen = self.parseRtcConstraints(configService.getClientValue('rtcConstraintsScreen'));

            // update stopVideoBelowMin from URL params.
            var stopVideoBelowMin = configService.getClientValue('stopVideoBelowMin');
            if (stopVideoBelowMin == 'true') {
                // for backward compatibility of URL params
                if (!self.rtcConstraintsVideo) {
                    self.rtcConstraintsVideo = [];
                }
                self.rtcConstraintsVideo.push({ googSuspendBelowMinBitrate: true });
            }

            // default audio and video constraints should include googDscp, unless useDscp=false is supplied or constraints are specified.
            var useDscp = configService.getClientValue('useDscp');
            if (!useDscp || useDscp != 'false') {
                if (!self.rtcConstraintsAudio) {
                    self.rtcConstraintsAudio = [{ googDscp: true }];
                }
                if (!self.rtcConstraintsVideo) {
                    self.rtcConstraintsVideo = [{ googDscp: true }];
                }
                if (!self.rtcConstraintsScreen) {
                    self.rtcConstraintsScreen = [{ googDscp: true }];
                }
            }

            // audioNacks - default false
            // enables NACKs for audio codec in offered SDP
            // First we check userpair and then can override it with URL param
            self.audioNacks = meetingService.getAudioNacks() || false;
            var audioNacks = configService.getClientValue('audioNacks');
            if (audioNacks == 'true' || audioNacks == 'false') {
                self.audioNacks = audioNacks == 'true';
            }

            // audioStereo - default false
            self.audioStereo = configService.getValue('audioStereo') == 'true';

            // try to use TURN TCP 443 when no good ice candidates - default true
            self.trackCandidates = configService.getValue('trackCandidates') !== 'false';
        }

        function getSupportedVideoCodec() {
            return ['auto', 'vp8', 'vp9', 'h264'];
        }

        // parse a text of the form name=value,name=value,... to an array of object with name, value
        // and convert true, false, number or float correctly. Return null if empty.
        function parseRtcConstraints(value) {
            if (!value) {
                return null;
            }

            if (value.substr(0, 1) == '{' && value.substr(value.length - 1) == '}') {
                try {
                    return JSON.parse(value);
                } catch (error) {
                    logger.error('parseRtcConstraints', 'failed to parse rtcConstraints', value, error);
                    return null;
                }
            }

            return value
                .split(',')
                .map(function (nameValue) {
                    var match = nameValue.match(/^([^=]+)=([^&#]*)$/);
                    if (match) {
                        var name = match[1],
                            value = match[2];
                        var data = {};
                        if (value == 'true' || value == 'false') {
                            value = value == 'true';
                        } else if (value == 'null') {
                            value = null;
                        } else if (value.match(/^\-?\d+$/)) {
                            value = parseInt(value);
                        } else if (value.match(/^\-?\d+\.\d+$/)) {
                            value = parseFloat(value);
                        }
                        data[name] = value;
                        return data;
                    } else {
                        return null;
                    }
                })
                .filter(function (data) {
                    return !!data;
                });
        }

        function getH264ProfileLevelAttr(is_screen) {
            return self.videocodec == 'h264'
                ? 'profile-level-id=' + (is_screen ? self.h264ProfileLevel.screen : self.h264ProfileLevel.video)
                : undefined;
        }

        // callback from Janus, to customize SDP. Called for video publish currently.
        // If no_bitrate is set, then do not apply the min, max or start bitrate attributes.
        // If is_screen, then use the the max quant for screen share.
        // If is_sip is present, then it just adjusts the video codecs, and nothing else.
        // is_receive is only valid for is_sip, and indicates receive direction.
        // eslint-disable-next-line complexity
        function customizeSdpOld(jsep, no_bitrate, is_screen, is_sip, is_receive) {
            var sdp = jsep.sdp;

            var sdpLines = sdp.split('\r\n');
            logService.log(
                'videoroomCtrl customizeSdp is_screen=' +
                    !!is_screen +
                    ' is_sip=' +
                    !!is_sip +
                    ' is_receive=' +
                    !!is_receive
            );

            if (is_sip) {
                // if videocodec is set, update SDP to include only that codec.
                if (
                    (self.videocodec && self.videocodec != 'auto') ||
                    self.audiocodec ||
                    self.audioBitrate ||
                    self.audioNacks ||
                    self.audioStereo
                ) {
                    if (self.videocodec && self.videocodec != 'auto') {
                        logService.log('videoroomCtrl customizeSdp calling for video ' + self.videocodec);
                        if (
                            !customizeSdpRemoveOthers(
                                sdpLines,
                                'video',
                                self.videocodec.toUpperCase() + '/90000',
                                self.getH264ProfileLevelAttr(is_screen)
                            )
                        ) {
                            // this is not needed, but for backward compatibility with old code.
                            return sdp;
                        }
                    }

                    if (!is_screen) {
                        var codecLine, codecPayload;
                        var codecStr = 'OPUS/48000';
                        if (self.audiocodec) {
                            codecStr = self.audiocodec.toUpperCase() + '/';
                            if (self.audiocodec == 'opus') {
                                codecStr += '48000';
                            } else if (self.audiocodec == 'pcmu' || self.audiocodec == 'pcma') {
                                codecStr += '8000';
                            } else if (self.audiocodec == 'isac16') {
                                codecStr = 'ISAC/16000';
                            } else if (self.audiocodec == 'isac32') {
                                codecStr = 'ISAC/32000';
                            }
                            logService.log('videoroomCtrl customizeSdp calling for audio ' + codecStr);
                            customizeSdpRemoveOthers(sdpLines, 'audio', codecStr);
                        }

                        // Adding NACKs for an audio codec
                        if (self.audioNacks && !is_receive) {
                            codecLine = findLine(sdpLines, 'a=rtpmap', codecStr);
                            if (codecLine > 0) {
                                codecPayload = getCodecPayloadType(sdpLines[codecLine]);
                                var nackLine = findLine(sdpLines, 'a=rtcp-fb:' + codecPayload.toString(), 'nack');
                                if (!nackLine) {
                                    logService.log('videoroomCtrl customizeSdp added NACKs for ' + codecStr);
                                    sdpLines[codecLine] =
                                        sdpLines[codecLine] + '\r\na=rtcp-fb:' + codecPayload.toString() + ' nack';
                                } else {
                                    // already added
                                    logService.log(
                                        'videoroomCtrl customizeSdp not added NACKs, as already present, for ' +
                                            codecStr
                                    );
                                }
                            }
                        }

                        // adding stereo and sprop-stereo
                        if (self.audioStereo) {
                            codecLine = findLine(sdpLines, 'a=rtpmap', codecStr);
                            if (codecLine > 0) {
                                codecPayload = getCodecPayloadType(sdpLines[codecLine]);
                                var newAttrs3 = 'stereo=1',
                                    newAttrs4 = 'stereo=1;sprop-stereo=1';
                                var fmtpLineIndex3 = findLine(sdpLines, 'a=fmtp:' + codecPayload);
                                if (fmtpLineIndex3 > 0) {
                                    var fmtpLineIndex4 = findLine(sdpLines, 'a=fmtp:' + codecPayload, newAttrs3);
                                    if (!fmtpLineIndex4) {
                                        logService.log('videoroomCtrl customizeSdp modifying fmtp with stereo');
                                        sdpLines[fmtpLineIndex3] = sdpLines[fmtpLineIndex3].concat(
                                            ';' + newAttrs4 + ';'
                                        );
                                    } else {
                                        logService.log(
                                            'videoroomCtrl customizeSdp not adding stereo= as already exists'
                                        );
                                    }
                                } else {
                                    logService.log('videoroomCtrl customizeSdp adding fmtp with stereo');
                                    sdpLines.splice(codecLine, 0, 'a=fmtp:' + codecPayload + ' ' + newAttrs4 + ';');
                                }
                            }
                        }

                        if (!no_bitrate && self.audioBitrate) {
                            if (!self.audiocodec || self.audiocodec == 'opus') {
                                logService.log('videoroomCtrl customizeSdp adjust audio bitrate');
                                var mediaLine = findLine(sdpLines, 'm=audio');
                                if (mediaLine > 0) {
                                    sdpLines.splice(mediaLine + 1, 0, 'b=AS:' + Math.floor(self.audioBitrate / 1000));
                                    if (!is_receive) {
                                        codecLine = findLine(sdpLines, 'a=rtpmap', 'OPUS/48000');
                                        if (codecLine > 0) {
                                            codecPayload = getCodecPayloadType(sdpLines[codecLine]);
                                            var newAttrs1 = 'maxaveragebitrate=' + self.audioBitrate;
                                            var fmtpLineIndex1 = findLine(sdpLines, 'a=fmtp:' + codecPayload);
                                            if (fmtpLineIndex1 > 0) {
                                                var fmtpLineIndex2 = findLine(
                                                    sdpLines,
                                                    'a=fmtp:' + codecPayload,
                                                    newAttrs1
                                                );
                                                if (!fmtpLineIndex2) {
                                                    sdpLines[fmtpLineIndex1] = sdpLines[fmtpLineIndex1].concat(
                                                        ';' + newAttrs1 + ';'
                                                    );
                                                } else {
                                                    logService.log(
                                                        'videoroomCtrl customizeSdp not adding maxaveragebitrate= as already exists'
                                                    );
                                                }
                                            } else {
                                                sdpLines.splice(
                                                    codecLine,
                                                    0,
                                                    'a=fmtp:' + codecPayload + ' ' + newAttrs1 + ';'
                                                );
                                            }
                                        }
                                    }
                                }
                            } else {
                                logger.warn(
                                    'customizeSdp',
                                    'ignored audioBitrate if audiocodec is not opus',
                                    self.audiocodec
                                );
                            }
                        }
                    }
                } else {
                    // Do nothing
                    return sdp;
                }
            } else {
                if (self.mediapath == 'sfu' && !is_screen && is_receive) {
                    // update audio here
                    //for correct parameters ALWAYS consult with Media Team and freeswitch/config/autoload_configs/opus.conf.xml
                    var mediaLine1 = findLine(sdpLines, 'm=audio');
                    if (mediaLine1 > 0) {
                        if (!no_bitrate && self.audioBitrate) {
                            sdpLine.splice(mediaLine1 + 1, 0, 'b=AS:' + Math.floor(self.audioBitrate / 1000));
                        }
                        var codecLine1 = findLine(sdpLines, 'a=rtpmap', 'OPUS/48000');
                        if (codecLine1 > 0) {
                            var codecPayload1 = getCodecPayloadType(sdpLines[codecLine1]);
                            var fmtpLine1 = findLine(sdpLines, 'a=fmtp:' + codecPayload1);
                            var newAttr1 = [
                                'maxaveragebitrate=' + (self.audioBitrate || 55000),
                                'maxplaybackrate=48000',
                                'sprop-maxcapturerate=48000',
                                'minptime=10'
                            ];
                            if (fmtpLine1 > 0) {
                                sdpLines[fmtpLine1] = sdpLines[fmtpLine1].concat(';' + newAttr1.join(';') + ';');
                            } else {
                                sdpLines.splice(
                                    codecLine1,
                                    0,
                                    'a=fmtp:' + codecPayload1 + ' ' + newAttr1.join(';') + ';'
                                );
                            }
                        }

                        // do this here, so that subsequent return does not change this part.
                        jsep.sdp = sdpLines.join('\r\n');
                    }
                }

                // for h264, reorder to put 42e01f before others.
                if (self.videocodec == 'h264') {
                    customizeSdpReorderVideo(sdpLines, self.getH264ProfileLevelAttr(is_screen));
                }

                // Find vp8 payload.
                var codec = self.videocodec == 'h264' ? 'H264' : 'VP8';
                var vp8Index = findLine(sdpLines, 'a=rtpmap', codec + '/90000'),
                    vp8Payload;
                if (!vp8Index) {
                    logger.warn('customizeSdp', `${codec}/90000 was not found, ignored`);
                    return sdp;
                } else {
                    vp8Payload = getCodecPayloadType(sdpLines[vp8Index]);
                }

                // Find the payload in fmtp line.
                var fmtpLineIndex = findLine(sdpLines, 'a=fmtp:' + vp8Payload.toString());

                var existingLine = null;
                if (fmtpLineIndex !== null) {
                    existingLine = sdpLines[fmtpLineIndex];
                }

                // create an fmtp value
                var newAttrs = [];
                if (!(is_screen && self.fileplay)) {
                    if (!existingLine || existingLine.indexOf('x-google-max-quantization=') < 0) {
                        newAttrs.push(
                            'x-google-max-quantization=' + (!is_screen ? self.maxQuantVideo : self.maxQuantScreen)
                        );
                    }

                    if (!no_bitrate) {
                        // do not apply min, max and start bitrate
                        var minBitrate = self.bitrate.adjust && !self.bitrate.altmax ? self.bitrate.min : 300000;
                        //newLines.push('x-google-max-bitrate=' + Math.round(self.getVideoBitrate() / 1000)); // do not set here.
                        if (!existingLine || existingLine.indexOf('x-google-min-bitrate=') < 0) {
                            newAttrs.push('x-google-min-bitrate=' + Math.round(minBitrate / 1000));
                        }
                        if (!existingLine || existingLine.indexOf('x-google-start-bitrate=') < 0) {
                            newAttrs.push('x-google-start-bitrate=' + Math.round(self.getVideoBitrate() / 1000));
                        }
                    }
                }

                newAttrs = newAttrs.length > 0 ? newAttrs.join(';') : '';

                if (newAttrs) {
                    if (fmtpLineIndex === null) {
                        sdpLines[vp8Index] =
                            sdpLines[vp8Index] + '\r\na=fmtp:' + vp8Payload.toString() + ' ' + newAttrs + ';';
                    } else {
                        sdpLines[fmtpLineIndex] = existingLine.concat(
                            (existingLine.endsWith(';') ? '' : ';') + newAttrs + ';'
                        );
                    }
                }

                logService.log('videoroomCtrl customizeSdp newAttrs=' + newAttrs);
            }

            jsep.sdp = sdpLines.join('\r\n');

            if (self.audiocodec || self.videocodec || self.audioBitrate) {
                // do not log always.
                logService.log('videoroomCtrl customizeSdp result=\n' + jsep.sdp);
            }
        }

        // customize SDP of subscriber side received from Janus.
        // It removes TWCC, so that browser will use REMB exclusively, and report availableIncomingBandwidth in stats.
        function customizeSdpSubscribeOld(jsep) {
            var sdp = jsep.sdp;
            if (sdp) {
                var sdpLines = sdp.split('\r\n');
                var twcc = 'http://www.ietf.org/id/draft-holmer-rmcat-transport-wide-cc-extensions-01'; // remove twcc line.
                var index = findLine(sdpLines, 'a=extmap:', twcc);
                if (index > 0) {
                    sdpLines.splice(index, 1);
                    jsep.sdp = sdpLines.join('\r\n');
                }
            }
        }

        function customizeSdpRemoveOthers(sdpLines, mediaType, codecType, attrType) {
            var codecLine;
            if (!attrType) {
                codecLine = findLine(sdpLines, 'a=rtpmap', codecType);
            } else {
                // match both codecType and attr
                var startLine = 0;
                while (true) {
                    codecLine = findLineInRange(sdpLines, startLine, -1, 'a=rtpmap', codecType);
                    if (!codecLine) {
                        break;
                    }
                    startLine = codecLine + 1;
                    var codecPayload1 = getCodecPayloadType(sdpLines[codecLine]);
                    if (codecPayload1) {
                        var attrLine = findLineInRange(sdpLines, startLine, -1, 'a=fmtp:' + codecPayload1, attrType);
                        if (attrLine) {
                            // found.
                            break;
                        }
                    }
                }
            }

            if (!codecLine) {
                logService.log('videoroomCtrl customizeSdp ' + codecType + ' was not found, ignored');
                return false;
            }
            var codecPayload = getCodecPayloadType(sdpLines[codecLine]);
            var mediaLine = findLine(sdpLines, 'm=' + mediaType);
            var nextLine = findLineInRange(sdpLines, mediaLine + 1, -1, 'm=');

            if (codecPayload && mediaLine) {
                var match1 = sdpLines[mediaLine].match(
                    mediaType == 'audio' ? /^m=audio\s+(\d+)\s+([^\s]+)\s+(.*)$/ : /^m=video\s+(\d+)\s+([^\s]+)\s+(.*)$/
                );
                var payloads = [codecPayload];
                sdpLines[mediaLine] = 'm=' + mediaType + ' ' + match1[1] + ' ' + match1[2] + ' ' + payloads.join(' ');

                // remove lines with a=rtpmap, rtcp-fb, fmtp for other codecs, so that only one is present.
                for (var i = nextLine ? nextLine - 1 : sdpLines.length - 1; i > mediaLine; --i) {
                    var line = sdpLines[i];
                    var match2 = line.match(/^a=(rtpmap|rtcp-fb|fmtp):(\d+)\s+/);
                    if (match2) {
                        if (match2[2] != '' + codecPayload) {
                            sdpLines.splice(i, 1);
                        }
                    }
                }
            } else {
                logger.warn('customizeSdpRemoveOthers', `${codecType} not found in SDP, ignored`);
            }
            return true;
        }

        // move video codecs matching the attribute to beginning.
        function customizeSdpReorderVideo(sdpLines, attrValue) {
            var mediaLine = findLine(sdpLines, 'm=video');
            var nextLine = findLineInRange(sdpLines, mediaLine + 1, -1, 'm=');
            if (mediaLine) {
                var match1 = sdpLines[mediaLine].match(/^m=video\s+(\d+)\s+([^\s]+)\s+(.*)$/);
                var payloads = match1[3].split(/\s+/);
                var found = []; // list of payload types that match attrValue in fmtp line.
                for (
                    var i = mediaLine + 1;
                    (nextLine !== null && i < nextLine) || (nextLine === null && i < sdpLines.length);
                    ++i
                ) {
                    var fmtpLine = sdpLines[i];
                    if (fmtpLine.startsWith('a=fmtp:') && fmtpLine.indexOf(attrValue) >= 0) {
                        var match2 = fmtpLine.match(/^a=fmtp:(\d+)\s/);
                        if (match2) {
                            found.push(match2[1]);
                        }
                    }
                }
                var preferred = []; // if found is valid, put in preferred.
                found.forEach(function (pt) {
                    var index1 = payloads.indexOf(pt);
                    if (index1 >= 0) {
                        preferred.push(pt);
                        payloads.splice(index1, 1);
                    }
                });
                payloads = preferred.slice().concat(payloads);

                sdpLines[mediaLine] = 'm=video ' + match1[1] + ' ' + match1[2] + ' ' + payloads.join(' ');

                // reorder related lines too, otherwise Janus picks the first one.
                var firstLine = -1; // first a=rtpmap of similar line index.
                var firstLines = [];
                for (
                    var j = mediaLine + 1;
                    (nextLine !== null && j < nextLine) || (nextLine === null && j < sdpLines.length);

                ) {
                    var attrLine = sdpLines[j];
                    var match3 = attrLine.match(/^a=(rtpmap|rtcp-fb|fmtp):(\d+)\s+/);
                    if (match3 && firstLine < 0) {
                        firstLine = j;
                    }
                    if (match3 && preferred.indexOf(match3[2]) >= 0) {
                        firstLines.push(attrLine);
                        sdpLines.splice(j, 1);
                    } else {
                        ++j;
                    }
                }
                var argArray = [firstLine, 0].concat(firstLines);
                sdpLines.splice.apply(sdpLines, argArray);
            } else {
                logger.warn('customizeSdpReorderVideo', 'video not found in SDP, ignored');
            }
        }

        //Helper functions aken from FSRTC.js

        // Find the line in sdpLines that starts with |prefix|, and, if specified,
        // contains |substr| (case-insensitive search).
        function findLine(sdpLines, prefix, substr) {
            return findLineInRange(sdpLines, 0, -1, prefix, substr);
        }

        // Find the line in sdpLines[startLine...endLine - 1] that starts with |prefix|
        // and, if specified, contains |substr| (case-insensitive search).
        function findLineInRange(sdpLines, startLine, endLine, prefix, substr) {
            var realEndLine = endLine != -1 ? endLine : sdpLines.length;
            for (var i = startLine; i < realEndLine; ++i) {
                if (sdpLines[i].indexOf(prefix) === 0) {
                    if (!substr || sdpLines[i].toLowerCase().indexOf(substr.toLowerCase()) !== -1) {
                        return i;
                    }
                }
            }
            return null;
        }

        // Gets the codec payload type from an a=rtpmap:X line.
        function getCodecPayloadType(sdpLine) {
            var pattern = new RegExp('a=rtpmap:(\\d+) \\w+\\/\\d+');
            var result = sdpLine.match(pattern);
            return result && result.length == 2 ? result[1] : null;
        }

        function findRtp(parsedSdp, codecStr) {
            let mediaIndex = null;
            let requiredRtp = null;
            for (let i = 0; i < parsedSdp.media.length; ++i) {
                requiredRtp = parsedSdp.media[i].rtp.find(
                    r => r.codec.toLowerCase() + '/' + r.rate === codecStr.toLowerCase()
                );
                if (requiredRtp) {
                    mediaIndex = i;
                    break;
                }
            }
            return [requiredRtp, mediaIndex];
        }

        function findFmtp(parsedSdp, payload, config = null) {
            let fmtpIndex = null;
            let requiredFmtp = null;
            for (let i = 0; i < parsedSdp.media.length; ++i) {
                if (config) {
                    requiredFmtp = parsedSdp.media[i].fmtp.find((f, index) => {
                        if (f.payload == payload && f.config && f.config.indexOf(config) !== -1) {
                            fmtpIndex = index;
                            return true;
                        }
                    });
                } else {
                    requiredFmtp = parsedSdp.media[i].fmtp.find((f, index) => {
                        if (f.payload == payload) {
                            fmtpIndex = index;
                            return true;
                        }
                    });
                }
                if (requiredFmtp) {
                    break;
                }
            }
            return [requiredFmtp, fmtpIndex];
        }

        function findRtcpFb(parsedSdp, type, payload) {
            let mediaIndex = null;
            let requiredRtcpFb = null;
            for (let i = 0; i < parsedSdp.media.length; ++i) {
                requiredRtcpFb = parsedSdp.media[i].rtcpFb.find(r => r.type === type && r.payload == payload);
                if (requiredRtcpFb) {
                    mediaIndex = i;
                    break;
                }
            }
            return [requiredRtcpFb, mediaIndex];
        }

        function addFmtp(parsedSdp, fmtpObj, index) {
            let fmtpArr = parsedSdp.media[index].fmtp;
            if (!fmtpArr) {
                fmtpArr = [];
            }
            fmtpArr.push(fmtpObj);
        }

        function addBandwidth(parsedSdp, bandwidthObj, index) {
            let bandwidthArr = parsedSdp.media[index].bandwidth;
            if (!bandwidthArr) {
                bandwidthArr = [];
            }
            bandwidthArr.push(bandwidthObj);
        }

        function addRtcpFb(parsedSdp, rtcpFbObj, index) {
            let rtcpFbArr = parsedSdp.media[index].rtcpFb;
            if (!rtcpFbArr) {
                rtcpFbArr = [];
            }
            rtcpFbArr.push(rtcpFbObj);
        }

        function addNacksForAudioCodec(parsedSdp, is_receive, codecStr) {
            if (self.audioNacks && !is_receive) {
                let [requiredRtp, rtpMediaIndex] = findRtp(parsedSdp, codecStr);
                if (requiredRtp) {
                    let codecPayload = requiredRtp.payload;
                    let nackLine = null;
                    [nackLine] = findRtcpFb(parsedSdp, 'nack', codecPayload);
                    if (!nackLine) {
                        logger.log('addNacksForAudioCodec', 'customizeSdpUsingParser added NACKs for ' + codecStr);
                        let rtcpFbObj = {
                            payload: codecPayload,
                            type: 'nack'
                        };
                        addRtcpFb(parsedSdp, rtcpFbObj, rtpMediaIndex);
                    }
                }
            }
        }

        function addStereo(parsedSdp, codecStr) {
            if (self.audioStereo) {
                let [requiredRtp, rtpMediaIndex] = findRtp(parsedSdp, codecStr);
                if (requiredRtp) {
                    let codecPayload = requiredRtp.payload;
                    if (codecPayload !== null) {
                        let newAttrs3 = 'stereo=1',
                            newAttrs4 = 'stereo=1;sprop-stereo=1';
                        let [fmtpObj] = findFmtp(parsedSdp, codecPayload);
                        if (fmtpObj) {
                            let [fmtpObj2] = findFmtp(parsedSdp, codecPayload, newAttrs3);
                            if (!fmtpObj2) {
                                logger.log('customizeSdpUsingParser', 'modifying fmtp with stereo');
                                fmtpObj.config = fmtpObj.config.concat(';' + newAttrs4 + ';');
                            } else {
                                logger.log('customizeSdpUsingParser', 'not adding stereo= as already exists');
                            }
                        } else {
                            logger.log('customizeSdpUsingParser', 'adding fmtp with stereo');
                            addFmtp(
                                parsedSdp,
                                {
                                    payload: codecPayload,
                                    config: newAttrs4 + ';'
                                },
                                rtpMediaIndex
                            );
                        }
                    }
                }
            }
        }

        function adjustSipAudioBitrate(parsedSdp, no_bitrate) {
            if (!no_bitrate && self.audioBitrate) {
                if (!self.audiocodec || self.audiocodec === 'opus') {
                    logger.log('customizeSdpUsingParser', 'adjust audio bitrate');
                    let mediaIndex = parsedSdp.media.findIndex(m => m.type === 'audio');
                    if (mediaIndex !== -1) {
                        addBandwidth(
                            parsedSdp,
                            {
                                type: 'AS',
                                limit: Math.floor(self.audioBitrate / 1000)
                            },
                            mediaIndex
                        );
                        if (!is_receive) {
                            let [requiredRtp, mediaIndexOfRtp] = findRtp(parsedSdp, 'OPUS/48000');
                            if (requiredRtp) {
                                let codecPayload = requiredRtp.payload;
                                let newAttrs1 = 'maxaveragebitrate=' + self.audioBitrate;
                                let [fmtpObj] = findFmtp(parsedSdp, codecPayload);
                                if (fmtpObj) {
                                    let [fmtpObj2] = findFmtp(parsedSdp, codecPayload, newAttrs1);
                                    if (!fmtpObj2) {
                                        fmtpObj.config = fmtpObj.config.concat(';' + newAttrs1 + ';');
                                    } else {
                                        logger.log(
                                            'customizeSdpUsingParser',
                                            'not adding maxaveragebitrate= as already exists'
                                        );
                                    }
                                } else {
                                    addFmtp(
                                        parsedSdp,
                                        {
                                            payload: codecPayload,
                                            config: newAttrs1 + ';'
                                        },
                                        mediaIndexOfRtp
                                    );
                                }
                            }
                        }
                    }
                } else {
                    logger.warn(
                        'customizeSdpUsingParser',
                        'ignored audioBitrate if audiocodec is not opus',
                        self.audiocodec
                    );
                }
            }
        }

        function adjustAudioBitrate(jsep, parsedSdp, no_bitrate) {
            let mediaObj = parsedSdp.media.find(m => m.type === 'audio');
            if (mediaObj) {
                if (!no_bitrate && self.audioBitrate) {
                    addBandwidth(
                        parsedSdp,
                        {
                            type: 'AS',
                            limit: Math.floor(self.audioBitrate / 1000)
                        },
                        mediaObj
                    );
                }
                let [requiredRtp, mediaIndex] = findRtp(parsedSdp, 'OPUS/48000');

                if (requiredRtp) {
                    let codecPayload1 = requiredRtp.payload;
                    let [fmtpObj] = findFmtp(parsedSdp, codecPayload1);

                    let newAttr1 = [
                        'maxaveragebitrate=' + (self.audioBitrate || 55000),
                        'maxplaybackrate=48000',
                        'sprop-maxcapturerate=48000',
                        'minptime=10'
                    ];
                    if (fmtpObj) {
                        fmtpObj.config = fmtpObj.config.concat(';' + newAttr1.join(';') + ';');
                    } else {
                        addFmtp(
                            parsedSdp,
                            {
                                payload: codecPayload1,
                                config: newAttr1.join(';') + ';'
                            },
                            mediaIndex
                        );
                    }
                }
                jsep.sdp = sdpTransformService.write(parsedSdp);
            }
        }

        function getAudioCodecStr() {
            let codecStr = self.audiocodec.toUpperCase() + '/';
            switch (self.audiocodec) {
                case 'opus':
                    codecStr += '48000';
                    break;
                case 'pcmu':
                case 'pcma':
                    codecStr += '8000';
                    break;
                case 'isac16':
                    codecStr = 'ISAC/16000';
                    break;
                case 'isac32':
                    codecStr = 'ISAC/32000';
                    break;
                default:
                    break;
            }
            return codecStr;
        }

        function getQuantizationAndBitrateAttrs(existingLine, is_screen, no_bitrate) {
            let newAttrs = [];
            if (!(is_screen && self.fileplay)) {
                if (
                    !existingLine ||
                    (existingLine.config && existingLine.config.indexOf('x-google-max-quantization=') < 0)
                ) {
                    newAttrs.push(
                        'x-google-max-quantization=' + (!is_screen ? self.maxQuantVideo : self.maxQuantScreen)
                    );
                }

                if (!no_bitrate) {
                    // do not apply min, max and start bitrate
                    let minBitrate = self.bitrate.adjust && !self.bitrate.altmax ? self.bitrate.min : 300000;
                    if (
                        !existingLine ||
                        (existingLine.config && existingLine.config.indexOf('x-google-min-bitrate=') < 0)
                    ) {
                        newAttrs.push('x-google-min-bitrate=' + Math.round(minBitrate / 1000));
                    }
                    if (
                        !existingLine ||
                        (existingLine.config && existingLine.config.indexOf('x-google-start-bitrate=') < 0)
                    ) {
                        newAttrs.push('x-google-start-bitrate=' + Math.round(self.getVideoBitrate() / 1000));
                    }
                }
            }

            newAttrs = newAttrs.length > 0 ? newAttrs.join(';') : '';
            return newAttrs;
        }

        function customizeSdpUsingParser(jsep, no_bitrate, is_screen, is_sip, is_receive) {
            let sdp = jsep.sdp;
            let parsedSdp = sdpTransformService.parse(jsep.sdp);
            // Keep this commented line for debugging.
            // logger.log('customizeSdpUsingParser', `initial value: ${jsep.sdp} ${parsedSdp}`);
            logger.log(
                'customizeSdpUsingParser',
                `is_screen=${!!is_screen} is_sip=${!!is_sip} is_receive=${!!is_receive}`
            );

            if (is_sip) {
                // if videocodec is set, update SDP to include only that codec.
                if (
                    (self.videocodec && self.videocodec !== 'auto') ||
                    self.audiocodec ||
                    self.audioBitrate ||
                    self.audioNacks ||
                    self.audioStereo
                ) {
                    if (self.videocodec && self.videocodec !== 'auto') {
                        logger.log('customizeSdpUsingParser', `calling for video ${self.videocodec}`);
                        if (
                            !customizeSdpRemoveOthersUsingParser(
                                parsedSdp,
                                'video',
                                self.videocodec.toUpperCase() + '/90000',
                                self.getH264ProfileLevelAttr(is_screen)
                            )
                        ) {
                            // this is not needed, but for backward compatibility with old code.
                            return sdp;
                        }
                    }

                    if (!is_screen) {
                        let codecStr = 'OPUS/48000';
                        if (self.audiocodec) {
                            codecStr = getAudioCodecStr();
                            customizeSdpRemoveOthersUsingParser(parsedSdp, 'audio', codecStr);
                        }
                        // Adding NACKs for an audio codec
                        addNacksForAudioCodec(parsedSdp, is_receive, codecStr);
                        // adding stereo and sprop-stereo
                        addStereo(parsedSdp, codecStr);
                        adjustSipAudioBitrate(parsedSdp, no_bitrate);
                    }
                } else {
                    // Do nothing
                    return sdp;
                }
            } else {
                if (self.mediapath === 'sfu' && !is_screen && is_receive) {
                    // update audio here
                    //for correct parameters ALWAYS consult with Media Team and freeswitch/config/autoload_configs/opus.conf.xml
                    adjustAudioBitrate(jsep, parsedSdp, no_bitrate);
                }

                // for h264, reorder to put 42e01f before others.
                if (self.videocodec === 'h264') {
                    customizeSdpReorderVideoUsingParser(parsedSdp, self.getH264ProfileLevelAttr(is_screen));
                }

                // Find video codec payload.
                let codec = self.videocodec === 'h264' ? 'H264' : 'VP8';
                let [vp8Index, vp8MediaIndex] = findRtp(parsedSdp, `${codec}/90000`);
                let vp8Payload;
                if (!vp8Index) {
                    logger.warn('customizeSdpUsingParser', `${codec}/90000 was not found, ignored`);
                    return sdp;
                } else {
                    vp8Payload = vp8Index.payload;
                }

                let [fmtpObj, fmtpLineIndex] = findFmtp(parsedSdp, vp8Payload);

                let existingLine = null;
                if (fmtpObj) {
                    existingLine = fmtpObj;
                }

                // create an fmtp value
                let newAttrs = getQuantizationAndBitrateAttrs(existingLine, is_screen, no_bitrate);

                if (newAttrs) {
                    if (fmtpLineIndex === null) {
                        addFmtp(
                            parsedSdp,
                            {
                                payload: vp8Payload,
                                config: newAttrs + ';'
                            },
                            vp8MediaIndex
                        );
                    } else {
                        parsedSdp.media[vp8MediaIndex].fmtp[fmtpLineIndex].config +=
                            (existingLine.config.endsWith(';') ? '' : ';') + newAttrs + ';';
                    }
                }

                logger.log('customizeSdpUsingParser', `newAttrs=${JSON.stringify(newAttrs)}`);
            }
            jsep.sdp = sdpTransformService.write(parsedSdp);

            if (self.audiocodec || self.videocodec || self.audioBitrate) {
                // do not log always.
                logger.log(
                    'customizeSdpUsingParser',
                    `result=
                    ${jsep.sdp}`
                );
            }
        }

        function customizeSdpRemoveOthersUsingParser(parsedSdp, mediaType, codecType, attrType) {
            let requiredRtp = null;
            if (!attrType) {
                [requiredRtp] = findRtp(parsedSdp, codecType);
            } else {
                // match both codecType and attr
                for (let i = 0; i < parsedSdp.media.length; i++) {
                    let mediaObj = parsedSdp.media[i];
                    let rtpObjs = mediaObj.rtp.filter(
                        r => r.codec.toLowerCase() + '/' + r.rate === codecType.toLowerCase()
                    );
                    requiredRtp = rtpObjs.find(r =>
                        mediaObj.fmtp.find(
                            f => f.payload === r.payload && f.config && f.config.indexOf(attrType) !== -1
                        )
                            ? true
                            : false
                    );
                    if (requiredRtp) {
                        break;
                    }
                }
            }
            if (!requiredRtp) {
                logger.log('customizeSdpRemoveOthersUsingParser', `${codecType} was not found, ignored`);
                return false;
            }
            let codecPayload = requiredRtp.payload;

            if (codecPayload >= 0) {
                let mediaObj = parsedSdp.media.find(m => m.type === mediaType);
                let payloads = [codecPayload];
                mediaObj.payloads = payloads.join(' ');

                mediaObj.rtp = mediaObj.rtp && mediaObj.rtp.filter(r => r.payload === codecPayload);
                mediaObj.fmtp = mediaObj.fmtp && mediaObj.fmtp.filter(r => r.payload === codecPayload);
                mediaObj.rtcpFb = mediaObj.rtcpFb && mediaObj.rtcpFb.filter(r => r.payload === codecPayload);
            } else {
                logger.warn('customizeSdpRemoveOthersUsingParser', `${codecType} not found in SDP, ignored`);
            }
            return true;
        }

        // customize SDP of subscriber side received from Janus.
        // It removes TWCC, so that browser will use REMB exclusively, and report availableIncomingBandwidth in stats.
        function customizeSdpSubscribeUsingParser(jsep) {
            let sdp = jsep.sdp;
            if (sdp) {
                const parsedSdp = sdpTransformService.parse(sdp);

                for (let i = 0; i < parsedSdp.media.length; i++) {
                    let mediaObj = parsedSdp.media[i];
                    if (mediaObj.ext) {
                        let filteredExt = mediaObj.ext.filter(
                            extension =>
                                extension.uri !==
                                'http://www.ietf.org/id/draft-holmer-rmcat-transport-wide-cc-extensions-01'
                        );
                        mediaObj.ext = filteredExt;
                    }
                }
                let modifiedSdp = sdpTransformService.write(parsedSdp);
                jsep.sdp = modifiedSdp;
                return modifiedSdp;
            }
        }

        function reorderArrayToPickPreferredPayload(array, preferredPayload) {
            let i = 0;
            for (let j = 0; j < array.length; j++) {
                let r = array[j];
                if (preferredPayload.includes(r.payload)) {
                    array.splice(j, 1);
                    array.splice(i, 0, r);
                    ++i;
                }
            }
        }

        // move video codecs matching the attribute to beginning.
        function customizeSdpReorderVideoUsingParser(parsedSdp, attrValue) {
            let videoMediaObj = null;

            videoMediaObj = parsedSdp.media.find(m => m.type === 'video');

            if (videoMediaObj) {
                let payloads = sdpTransformService.parsePayloads(videoMediaObj.payloads);
                let found = []; // list of payload types that match attrValue in fmtp line.
                let fmtpArray = videoMediaObj.fmtp;
                fmtpArray.forEach(fmtpLine => {
                    if (fmtpLine.config && fmtpLine.config.indexOf(attrValue) >= 0) {
                        found.push(fmtpLine.payload);
                    }
                });
                let preferred = []; // if found is valid, put in preferred.
                found.forEach(function (pt) {
                    let index1 = payloads.indexOf(pt);
                    if (index1 >= 0) {
                        preferred.push(pt);
                        payloads.splice(index1, 1);
                    }
                });
                payloads = preferred.slice().concat(payloads);
                videoMediaObj.payloads = payloads.join(' ');

                // reorder related lines too, otherwise Janus picks the first one.
                reorderArrayToPickPreferredPayload(videoMediaObj.rtp, preferred);
                reorderArrayToPickPreferredPayload(videoMediaObj.fmtp, preferred);
                reorderArrayToPickPreferredPayload(videoMediaObj.rtcpFb, preferred);
            } else {
                logger.warn('customizeSdpReorderVideoUsingParser', 'video not found in SDP, ignored');
            }
        }

        function compareArray(array1, array2) {
            if (array1.length !== array2.length) {
                return false;
            }
            let isEqual = array1.every(element => {
                if (array2.includes(element)) {
                    return true;
                } else {
                    // Keep these lines for debugging
                    // logger.error('compareArray', `SDP parser: element that does not match: ${element}`);
                    return false;
                }
            });
            return isEqual;
        }

        function customizeSdp(jsep, no_bitrate, is_screen, is_sip, is_receive) {
            if (self.useSdpParser === 'test') {
                let tempJsep = JSON.parse(JSON.stringify(jsep));
                logger.log('customizeSdp', `initial jsep: ${jsep}`);

                customizeSdpUsingParser(tempJsep, no_bitrate, is_screen, is_sip, is_receive);
                let sdpWithParser = tempJsep.sdp;

                customizeSdpOld(jsep, no_bitrate, is_screen, is_sip, is_receive);
                let sdpWithoutParser = jsep.sdp;

                let sdpLinesWithParser = sdpWithParser.split('\r\n');
                let sdpLinesWithoutParser = sdpWithoutParser.split('\r\n');

                if (compareArray(sdpLinesWithParser, sdpLinesWithoutParser)) {
                    logger.log('customizeSdp', 'SDP Parsed successfully with sdp-transform');
                    // Keep these lines for debugging
                    // logger.log('customizeSdp', 'sdpWithParser', sdpWithParser);
                    // logger.log('customizeSdp', 'sdpWithoutParser', sdpWithoutParser);
                } else {
                    logger.error('customizeSdp', 'SDP not Parsed correctly with sdp-transform');
                    logger.log('customizeSdp', `sdpWithParser ${sdpWithParser}`);
                    logger.log('customizeSdp', `sdpWithoutParser ${sdpWithoutParser}`);
                    self.msgflowEvent('warn:sdp-transform', {
                        reason: 'mismatch in publish SDP transform',
                        with: sdpWithParser,
                        without: sdpWithoutParser
                    });
                    analyticsWrapperService.eventTrack('Meeting - SDP Mismatch - starting', {
                        with: sdpWithParser,
                        without: sdpWithoutParser
                    });
                }
            } else if (self.useSdpParser) {
                customizeSdpUsingParser(jsep, no_bitrate, is_screen, is_sip, is_receive);
            } else {
                customizeSdpOld(jsep, no_bitrate, is_screen, is_sip, is_receive);
            }
        }

        function customizeSdpSubscribe(jsep) {
            if (self.enableAvailableIncomingBitrate) {
                if (self.useSdpParser === 'test') {
                    let tempJsep = JSON.parse(JSON.stringify(jsep));

                    customizeSdpSubscribeOld(jsep);
                    let sdpWithoutParser = jsep.sdp;
                    customizeSdpSubscribeUsingParser(tempJsep);
                    let sdpWithParser = tempJsep.sdp;

                    let sdpLinesWithParser = sdpWithParser.split('\r\n');
                    let sdpLinesWithoutParser = sdpWithoutParser.split('\r\n');

                    if (compareArray(sdpLinesWithParser, sdpLinesWithoutParser)) {
                        logger.log('[customizeSdpSubscribe]', 'SDP Parsed successfully with sdp-transform');
                        // Keep this for debugging.
                        // logger.log('[customizeSdpSubscribe]', `sdpWithParser ${sdpWithParser}`);
                        // logger.log('[customizeSdpSubscribe]', `sdpWithoutParser ${sdpWithoutParser}`);
                    } else {
                        logger.error('[customizeSdpSubscribe]', 'SDP not Parsed correctly with sdp-transform');
                        logger.log('[customizeSdpSubscribe]', `sdpWithParser ${sdpWithParser}`);
                        logger.log('[customizeSdpSubscribe]', `sdpWithoutParser ${sdpWithoutParser}`);
                        self.msgflowEvent('warn:sdp-transform', {
                            reason: 'mismatch in subscribe SDP transform',
                            with: sdpWithParser,
                            without: sdpWithoutParser
                        });
                        analyticsWrapperService.eventTrack('Meeting - SDP Mismatch - starting', {
                            with: sdpWithParser,
                            without: sdpWithoutParser
                        });
                    }
                } else if (self.useSdpParser) {
                    customizeSdpSubscribeUsingParser(jsep);
                } else {
                    customizeSdpSubscribeOld(jsep);
                }
            }
        }
    }
]);
